import Image from 'next/image'
import type { ReactNode } from 'react'
import { cn } from '@/components/ui'

/**
 * Le ticket de séance : un aplat lavande encoché, la date en grand, l'action
 * de rejoindre en bas.
 *
 * - `vertical` (accueil, fiche séance) : la date, les repères, l'intitulé, puis
 *   un pointillé et la ligne d'action ;
 * - `horizontal` (agenda, parcours) : l'intitulé à gauche, l'action à droite
 *   derrière un pointillé vertical — **quand son conteneur fait au moins
 *   48rem** (requête de conteneur `@3xl`) ; plus étroit, l'action passe
 *   dessous. C'est la largeur du ticket qui compte, pas celle de l'écran.
 *
 * ⚠️ **L'illustration se pose dans le coin bas, jamais sur le titre** — un
 * sticker sur un mot important est interdit par la charte. La ligne d'action
 * lui réserve sa place à droite. L'illustration vient du pack 2027
 * (`/brand/stickers/`) et se pose sur la lavande : c'est permis.
 *
 * ⚠️ `action` est le plus souvent le bouton jaune « Rejoindre la visio » —
 * l'action principale de l'écran. Il n'en faut pas d'autre en jaune à côté.
 */
export function Ticket({
  forme = 'vertical',
  surtitre,
  jour,
  reperes = [],
  titre,
  detail,
  note,
  action,
  illustration,
  className,
}: {
  forme?: 'vertical' | 'horizontal'
  surtitre?: string
  /** « Jeudi 30 octobre » — la date en toutes lettres, majuscule comprise. */
  jour: string
  /** Les repères en pastilles blanches : l'horaire, « En visio »… */
  reperes?: ReactNode[]
  titre: ReactNode
  detail?: ReactNode
  /** La phrase courte de la ligne d'action (« Le lien s'ouvre 10 minutes avant »). */
  note?: ReactNode
  action?: ReactNode
  /** Un fichier de `/brand/stickers/`. */
  illustration?: string
  className?: string
}) {
  const vertical = forme === 'vertical'

  const tete = (
    /* `pb-6` sur le vertical : sans hauteur imposée (au téléphone), le
       pointillé, poussé par `mt-auto`, venait coller au titre. */
    <div className={cn('min-w-0', vertical && 'pb-6')}>
      {surtitre && <span className="u-legende block text-ink">{surtitre}</span>}
      <p
        className={cn(
          'mt-2 font-black tracking-[-0.05em] leading-[0.9] text-ink',
          'text-[42px] oasis:text-[clamp(44px,3.8vw,88px)]',
        )}
      >
        {jour}
      </p>
      {reperes.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2.5">
          {reperes.map((r, i) => (
            <span
              key={i}
              className={cn(
                'rounded-pill bg-surface px-[13px] py-1.5 text-[14px] font-extrabold tracking-[-0.01em] text-ink',
                'shadow-[0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.18)]',
                i % 2 === 0 ? '-rotate-2' : 'rotate-2',
              )}
            >
              {r}
            </span>
          ))}
        </div>
      )}
      <h3 className="mt-6 text-[19px] leading-[1.15] font-extrabold tracking-[-0.02em] text-ink oasis:text-[clamp(19px,1.4vw,28px)]">
        {titre}
      </h3>
      {detail && <p className="mt-1 text-[15px] text-ink oasis:text-[clamp(15px,1vw,18px)]">{detail}</p>}
    </div>
  )

  const actionBloc = (note || action) && (
    <div
      className={cn(
        'flex flex-wrap items-center gap-4',
        vertical
          ? 'justify-between pr-[104px] oasis:pr-[clamp(120px,9vw,190px)]'
          : 'flex-col items-start border-t-2 border-dashed border-ink/35 pt-5 pr-[104px] @3xl:border-t-0 @3xl:border-l-2 @3xl:pt-0 @3xl:pl-10 @3xl:pr-[clamp(150px,11vw,220px)]',
      )}
    >
      {note && <p className="text-[14px] text-ink">{note}</p>}
      {action}
    </div>
  )

  return (
    <div className={cn('@container relative', className)}>
      <div
        className={cn(
          'u-ticket rounded-[24px] bg-lavender p-[26px_22px_24px] oasis:p-[34px]',
          vertical ? 'flex h-full flex-col' : 'u-ticket-horizontal grid gap-6 @3xl:grid-cols-[minmax(0,1fr)_auto] @3xl:items-end',
        )}
      >
        {tete}
        {vertical ? (
          <>
            <div aria-hidden className="mt-auto mb-5 -mx-[22px] border-t-2 border-dashed border-ink/35 pt-0 oasis:-mx-[34px]" />
            {actionBloc}
          </>
        ) : (
          actionBloc
        )}
      </div>
      {illustration && (
        <Image
          src={illustration}
          alt=""
          width={320}
          height={320}
          className="u-illustration absolute right-2.5 bottom-4 z-10 w-[104px] -rotate-6 oasis:w-[clamp(120px,8.5vw,180px)]"
        />
      )}
    </div>
  )
}
